import type { PagingFilter } from '@pbk-web/types';

import type { ApiResponse } from '../models/ApiResponse';
import type { FormDefaultConfiguration } from '../models/ClientFormDefaults';
import type { ClientFormStatus } from '../models/ClientFormStatus';
import type { ClientFormUserDto, FormAccessRoleGroupRequest, TemplateFormAccessResponse } from '../models/ClientFormUser';
import type { FormConfig, FormTemplatePagedFilter, GetAllTemplatesRequest } from '../models/Form';
import type { RecordColumnData, RecordRowData } from '../models/Record';
import type User from '../models/User';
import type { FormDefault } from './../models/ClientFormDefaults';
import type { ClientTemplateFormUser } from './../models/ClientTemplateFormUser';
import TemplateFormServiceBase from './TemplateFormServiceBase';

class ClientTemplateFormService extends TemplateFormServiceBase {
  _clientId: string;

  constructor(clientId: string) {
    super();
    this._clientId = clientId;
  }

  //#region Builder API

  public createTemplate(data: Record<string, unknown>): Promise<ApiResponse<FormConfig>> {
    return TemplateFormServiceBase.create(`/v1/clients/${this._clientId}/template/forms`, data);
  }

  public updateTemplate(formId: string, data: Record<string, unknown>): Promise<ApiResponse<FormConfig>> {
    return TemplateFormServiceBase.update(`/v1/clients/${this._clientId}/template/forms/${formId}`, data);
  }

  public archiveTemplate(formId: string): Promise<ApiResponse<boolean>> {
    return TemplateFormServiceBase.archive(`/v1/clients/${this._clientId}/template/forms/${formId}/archive`);
  }

  public restoreTemplate(formId: string): Promise<ApiResponse<boolean>> {
    return TemplateFormServiceBase.restore(`/v1/clients/${this._clientId}/template/forms/${formId}/restore`);
  }

  public getAllTemplates(request: GetAllTemplatesRequest): Promise<ApiResponse<FormConfig[]>> {
    return TemplateFormServiceBase.getAll(`/v1/clients/${this._clientId}/template/forms`, request);
  }

  public getAllTemplatesPaged(filter: FormTemplatePagedFilter): Promise<ApiResponse<FormConfig[]>> {
    return TemplateFormServiceBase.getAllPaged(`/v1/clients/${this._clientId}/template/forms/paged`, filter);
  }

  public getFormTemplate(
    formId: string,
    version: number | undefined = undefined,
    excludeSections: boolean | undefined = undefined,
  ): Promise<ApiResponse<FormConfig>> {
    return TemplateFormServiceBase.getById(`/v1/clients/${this._clientId}/template/forms/${formId}`, formId, version, excludeSections);
  }

  public getClientFormUsages(
    formId: string,
    version: number,
    clientFormStatuses: ClientFormStatus[],
  ): Promise<ApiResponse<Record<ClientFormStatus, number>>> {
    return TemplateFormServiceBase.getUsages(`/v1/clients/${this._clientId}/template/forms/${formId}/usages`, version, clientFormStatuses);
  }
  public getTemplateReferenceUsages(formId: string, version?: number): Promise<ApiResponse<number>> {
    return TemplateFormServiceBase.get(`/v1/clients/${this._clientId}/template/forms/${formId}/references?version=${version}`);
  }
  //#endregion

  public getAllUsedTemplates(request: GetAllTemplatesRequest): Promise<ApiResponse<FormConfig[]>> {
    return TemplateFormServiceBase.getAll(`/v1/clients/${this._clientId}/template/forms/used`, request);
  }

  public isLinkedToClient(formId: string): Promise<ApiResponse<boolean>> {
    return TemplateFormServiceBase.get(`/v1/clients/${this._clientId}/template/forms/${formId}/is-linked-to-client`);
  }

  //#region Defaults API

  public createFormDefaults(formId: string, configuration: FormDefaultConfiguration): Promise<ApiResponse<FormDefault>> {
    return TemplateFormServiceBase.post(`/v1/clients/${this._clientId}/template/forms/${formId}/defaults`, { configuration: configuration });
  }

  //#endregion

  //#region Resource APi

  public importTemplate(formId: string): Promise<ApiResponse<FormConfig>> {
    return TemplateFormServiceBase.post(`/v1/clients/${this._clientId}/template/forms/import`, { templateFormId: formId });
  }

  public getRecordsWithAnswers(formId: string, filter: PagingFilter): Promise<ApiResponse<Record<string, RecordColumnData | string>[]>> {
    return TemplateFormServiceBase.get<RecordRowData[]>(`/v1/clients/${this._clientId}/template/forms/${formId}/answers`, {
      params: filter,
      signal: new AbortController().signal,
    }).then((res) => {
      const data = [
        ...res.data.map((x) => {
          x.columns['id'] = x.id;
          x.columns['subTitle'] = x.subTitle;
          return x.columns;
        }),
      ];
      return {
        ...res,
        data: data,
      };
    });
  }

  //#endregion

  //#region Users

  public getUsers(formId: string): Promise<ApiResponse<ClientFormUserDto[]>> {
    return TemplateFormServiceBase.get(`/v1/clients/${this._clientId}/template/forms/${formId}/users`);
  }

  public addOrUpdateUsers(formId: string, users: ClientFormUserDto[]): Promise<ApiResponse<ClientTemplateFormUser[]>> {
    return TemplateFormServiceBase.post(`/v1/clients/${this._clientId}/template/forms/${formId}/users`, users);
  }

  public removeUser(formId: string, userId: string): Promise<ApiResponse<ClientTemplateFormUser[]>> {
    return TemplateFormServiceBase.delete(`/v1/clients/${this._clientId}/template/forms/${formId}/users`, { params: { userId: userId } });
  }

  public getCurrentResourceTemplateUser(formTemplateUsers: ClientTemplateFormUser[], currentUser: User): ClientTemplateFormUser | null {
    return formTemplateUsers.find((user) => user.id === currentUser.id) || null;
  }

  //#endregion

  //#region Access (policy-based members)

  /** Policy-based access for a resource, grouped by role with each rule's currently-resolved users. */
  public getAccess(formId: string): Promise<ApiResponse<TemplateFormAccessResponse>> {
    return TemplateFormServiceBase.get(`/v1/clients/${this._clientId}/template/forms/${formId}/access`);
  }

  /** Write one role's full policy set (replace semantics for that role, other roles untouched). */
  public addOrUpdateAccess(formId: string, roleGroup: FormAccessRoleGroupRequest): Promise<ApiResponse<boolean>> {
    return TemplateFormServiceBase.post(`/v1/clients/${this._clientId}/template/forms/${formId}/access`, roleGroup);
  }

  //#endregion
}

export default ClientTemplateFormService;
